import React from "react";
import { motion } from "framer-motion";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { fadeUp, softReveal, staggerParent, viewportOnce } from "./motion";
import { renderVisual } from "./ServiceCard";
import { getServiceBySlug, getServiceSlugByPath, proofClients, whatsappUrl } from "../data/services";

const ServiceLanding = () => {
  const { serviceSlug } = useParams();
  const location = useLocation();
  const service = getServiceBySlug(serviceSlug || getServiceSlugByPath(location.pathname));

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  return (
    <motion.section
      className="service-page"
      variants={staggerParent(0.06, 0.1)}
      initial="hidden"
      whileInView="show"
      viewport={viewportOnce}
    >
      <motion.div className="service-page-hero" variants={softReveal}>
        <div className="service-page-copy">
          <p className="section-kicker">{service.shortTitle} Services</p>
          <h1>{service.routeTitle}</h1>
          <p>{service.summary}</p>

          <div className="service-page-actions">
            <a className="cta-button cta-primary" href={whatsappUrl} target="_blank" rel="noreferrer">
              WhatsApp Us
            </a>
            <Link className="cta-button cta-outline" to="/contact">
              Enquiry Form
            </Link>
          </div>
        </div>

        <div className="service-page-visual" aria-hidden="true">
          <p className="service-panel-kicker">{service.visualLabel}</p>
          {renderVisual(service.visualType, service.visualPoints)}
        </div>
      </motion.div>

      <motion.div className="service-page-grid" variants={staggerParent(0.1, 0.08)}>
        <motion.article className="service-page-card" variants={softReveal}>
          <p className="section-kicker">Benefits</p>
          <h2>What this helps improve</h2>
          <ul>
            {service.benefits.map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </ul>
        </motion.article>

        <motion.article className="service-page-card" variants={softReveal}>
          <p className="section-kicker">Process</p>
          <h2>How we approach it</h2>
          <ol>
            {service.process.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </motion.article>
      </motion.div>

      <motion.div className="service-proof-band" variants={softReveal}>
        <div>
          <p className="section-kicker">Proof Signals</p>
          <h2>Trusted across healthcare, food, and local business categories.</h2>
          <p>
            dYzital supports businesses that need clearer positioning, stronger digital visibility, and practical lead generation systems.
          </p>
        </div>

        <div className="service-proof-logos">
          {proofClients.map((client) => (
            <article key={client.name} className="service-proof-logo">
              <img src={client.logo} alt={`${client.name} logo`} loading="lazy" />
              <span>{client.industry}</span>
            </article>
          ))}
        </div>
      </motion.div>

      <motion.div className="service-faq-section" variants={staggerParent(0.1, 0.08)}>
        <motion.div className="service-faq-intro" variants={fadeUp}>
          <p className="section-kicker">FAQs</p>
          <h2>Questions businesses ask before starting</h2>
        </motion.div>

        <div className="service-faq-grid">
          {service.faqs.map((item) => (
            <motion.article key={item.question} className="service-faq-card" variants={softReveal}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </motion.article>
          ))}
        </div>
      </motion.div>

      <motion.div className="service-final-cta" variants={softReveal}>
        <h2>Ready to improve your {service.shortTitle} results?</h2>
        <div className="service-page-actions">
          <a className="cta-button cta-primary" href={whatsappUrl} target="_blank" rel="noreferrer">
            Chat on WhatsApp
          </a>
          <Link className="cta-button cta-outline" to="/contact">
            Send Enquiry
          </Link>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default ServiceLanding;
